/**
 * ===========================================================================
 *  FRONT END — title, warrior select, battlefield select, muster,
 *              pause, options, results
 * ===========================================================================
 *
 * All seven screens share one modal shell so they cannot fight each other, and
 * every one of them is keyboard AND pointer driven (arrows/W-S + Enter, or a
 * click). Widgets are hand-built: a native <input type=range> or <select> is
 * an instant "browser demo" tell, and their look cannot be controlled.
 *
 * THE AUDIO GESTURE
 * WebAudio refuses to start without a real user gesture, so the shipped game
 * would be silent forever unless the UI unlocks it. `begin()` on the title
 * screen is that gesture: it calls `world.audio.unlock()` and starts the score.
 * (docs/REQUESTS.md, audio -> ui.)
 *
 * THE ROUTE TO THE FIELD
 * Title -> WARRIOR -> BATTLEFIELD -> MUSTER -> playing, which is the order
 * Dynasty Warriors uses and the order this game's own systems want: the
 * warrior decides the moveset, the battlefield decides the light and the
 * landing point, and both have to be committed before `mission.start()` fires
 * on the first frame the UI reports 'playing'. The three middle screens are
 * drawn by `select.js`; this file owns the state machine, the keyboard, and
 * `muster()` — the one place a choice becomes the battle. Escape walks back up
 * the same chain, one screen at a time.
 *
 * HEADLESS
 * The capture browser never gets a gesture and must never see a modal it did
 * not ask for, so nothing here opens by itself when `CFG.debug.headless` is
 * set — but every screen can still be opened explicitly by a scenario through
 * `world.ui.setState()` / `world.ui.showResults()`.
 */
import { CFG, QUALITY, applyQuality } from '../core/config.js';
import { fit, numeral, crest } from './gfx.js';
import { createSelect } from './select.js';
import { applyLoadout } from '../game/roster.js';

const QUAL = ['potato', 'low', 'medium', 'high', 'ultra'];
/** Display 0..1 maps onto a per-bus ceiling, so "40 %" is a sane music mix. */
const BUS_MAX = { master: 1.0, music: 0.5, sfx: 1.0, voice: 0.9, ambience: 0.6 };

export function createMenus(host, deps) {
  const { world, settings, sound, onState } = deps;
  const headless = !!CFG.debug.headless;

  const el = document.createElement('div');
  el.className = 'menus';
  el.innerHTML = `
    <div class="modal title" data-k="title">
      <div class="scrim"></div>
      <div class="body">
        <canvas class="crest"></canvas>
        <div class="mtitle ser">NUSANTARA WARRIOR</div>
        <div class="mrule"></div>
        <div class="msub">The Oath of a Thousand Isles</div>
        <div class="menu">
          <button type="button" class="btn" data-a="begin">Begin the Battle</button>
          <button type="button" class="btn" data-a="options">Options</button>
        </div>
        <div class="hint"><span class="hint-desktop">Enter — or click anywhere — to choose your warrior</span><span class="hint-touch">Tap to choose your warrior</span></div>
      </div>
      <div class="keys">
        <span class="keys-wide"><em>WASD</em> move &nbsp;·&nbsp; <em>J</em> attack &nbsp;·&nbsp; <em>K</em> charge &nbsp;·&nbsp;
          <em>L</em> musou &nbsp;·&nbsp; <em>Space</em> dodge &nbsp;·&nbsp; <em>Q</em> lock on &nbsp;·&nbsp;
          <em>Esc</em> pause</span>
        <span class="keys-compact"><em>WASD</em> move · <em>J/K</em> attack · <em>Space</em> dodge · <em>Esc</em> pause</span>
        <span class="keys-touch">Left stick moves · swipe the field to look · battle buttons attack and dodge</span>
      </div>
    </div>

    <div class="modal pause" data-k="pause">
      <div class="scrim"></div>
      <div class="body">
        <div class="msub">Battle Suspended</div>
        <div class="mtitle ser" style="font-size:calc(var(--u)*40);letter-spacing:calc(var(--u)*12)">PAUSED</div>
        <div class="mrule sm"></div>
        <div class="menu">
          <button type="button" class="btn" data-a="resume">Resume</button>
          <button type="button" class="btn" data-a="options">Options</button>
          <button type="button" class="btn" data-a="quit">Abandon Field</button>
        </div>
      </div>
    </div>

    <div class="modal opts" data-k="options">
      <div class="scrim"></div>
      <div class="body">
        <div class="msub">Options</div>
        <div class="mrule sm"></div>
        <div class="rows"></div>
        <div class="menu"><button type="button" class="btn" data-a="back">Back</button></div>
      </div>
    </div>

    <div class="modal res" data-k="results">
      <div class="scrim"></div>
      <div class="body">
        <div class="msub rsub">Battle Report</div>
        <div class="mtitle ser rtitle" style="font-size:calc(var(--u)*48);letter-spacing:calc(var(--u)*13)">VICTORY</div>
        <div class="mrule"></div>
        <div class="grid">
          <div class="cell"><canvas data-c="ko"></canvas><div class="k">Defeated</div></div>
          <div class="cell"><canvas data-c="chain"></canvas><div class="k">Best Chain</div></div>
          <div class="cell"><canvas data-c="bases"></canvas><div class="k">Bases Held</div></div>
          <div class="cell"><canvas data-c="time"></canvas><div class="k">Field Time</div></div>
        </div>
        <div class="rank">
          <div class="rk ser">S</div>
          <div class="rl"><b>Rank</b><span class="rnote">Peerless</span></div>
        </div>
        <div class="menu">
          <button type="button" class="btn" data-a="again">Fight It Again</button>
          <button type="button" class="btn" data-a="rechoose">Change Warrior or Field</button>
        </div>
      </div>
    </div>`;
  host.appendChild(el);

  const modals = {};
  el.querySelectorAll('.modal').forEach(m => { modals[m.dataset.k] = m; });
  const crestCv = el.querySelector('.crest');
  const rowsEl = el.querySelector('.opts .rows');
  const resCv = {
    ko: el.querySelector('[data-c="ko"]'), chain: el.querySelector('[data-c="chain"]'),
    bases: el.querySelector('[data-c="bases"]'), time: el.querySelector('[data-c="time"]'),
  };
  /* The three selection screens are their own module, but they live in this
   * modal set: one `modals` map means one thing can be open at a time, which
   * is the whole reason the shell is shared. */
  const select = createSelect(el, {
    world, sound,
    onConfirm: (from) => forward(from),
    onBack: (from) => backward(from),
  });
  for (const k in select.modals) modals[k] = select.modals[k];

  const rankEl = el.querySelector('.res .rk');
  const rankNote = el.querySelector('.res .rnote');
  const resTitle = el.querySelector('.res .rtitle');
  const resSub = el.querySelector('.res .rsub');

  /* ------------------------------------------------------- options widgets */

  const widgets = [];

  function addSeg(key, name, values, labels, get, set) {
    const row = document.createElement('div');
    row.className = 'row';
    row.innerHTML = `<div class="k">${name}</div><div class="v"></div>`;
    const v = row.querySelector('.v');
    const btns = values.map((val, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'seg';
      b.textContent = labels[i];
      b.addEventListener('pointerdown', (ev) => { ev.preventDefault(); set(val); sync(); sound('ui_click'); });
      b.addEventListener('pointerenter', () => sound('ui_hover'));
      v.appendChild(b);
      return b;
    });
    rowsEl.appendChild(row);
    widgets.push({ sync() { const cur = get(); btns.forEach((b, i) => b.classList.toggle('on', values[i] === cur)); } });
  }

  function addSlider(name, get, set, fmt, lo, hi) {
    const row = document.createElement('div');
    row.className = 'row';
    row.innerHTML = `<div class="k">${name}</div><div class="v"><div class="slide">
      <div class="trk"></div><div class="fl"></div><div class="kn"></div></div></div><div class="n">0</div>`;
    const sl = row.querySelector('.slide');
    const fl = row.querySelector('.fl');
    const kn = row.querySelector('.kn');
    const num = row.querySelector('.n');
    let dragging = false;
    const at = (ev) => {
      const r = sl.getBoundingClientRect();
      const t = Math.max(0, Math.min(1, (ev.clientX - r.left) / Math.max(1, r.width)));
      set(lo + (hi - lo) * t);
      sync();
    };
    sl.addEventListener('pointerdown', (ev) => { ev.preventDefault(); dragging = true; sl.setPointerCapture(ev.pointerId); at(ev); sound('ui_click'); });
    sl.addEventListener('pointermove', (ev) => { if (dragging) at(ev); });
    const stop = () => { dragging = false; };
    sl.addEventListener('pointerup', stop);
    sl.addEventListener('pointercancel', stop);
    sl.addEventListener('lostpointercapture', stop);
    rowsEl.appendChild(row);
    widgets.push({
      sync() {
        const t = Math.max(0, Math.min(1, (get() - lo) / (hi - lo)));
        fl.style.width = (t * 100).toFixed(1) + '%';
        kn.style.left = (t * 100).toFixed(1) + '%';
        num.textContent = fmt(get());
      },
    });
  }

  const pct = v => Math.round(v * 100) + '%';

  addSeg('quality', 'Quality', QUAL, ['Potato', 'Low', 'Med', 'High', 'Ultra'],
    () => settings.quality, (v) => deps.setQuality(v));
  addSlider('Master Volume', () => settings.master, v => deps.setVolume('master', v), pct, 0, 1);
  addSlider('Music', () => settings.music, v => deps.setVolume('music', v), pct, 0, 1);
  addSlider('Effects', () => settings.sfx, v => deps.setVolume('sfx', v), pct, 0, 1);
  addSlider('Look Sensitivity', () => settings.sensitivity,
    v => { settings.sensitivity = v; deps.save(); }, v => v.toFixed(2) + 'x', 0.25, 2.5);
  addSeg('vignette', 'Screen Effects', [true, false], ['On', 'Off'],
    () => settings.vignette, v => { settings.vignette = v; deps.save(); });
  addSeg('damage', 'Damage Numbers', [true, false], ['On', 'Off'],
    () => settings.damage, v => { settings.damage = v; deps.save(); });

  function sync() { for (let i = 0; i < widgets.length; i++) widgets[i].sync(); }

  /* --------------------------------------------------------------- state */

  let state = 'playing';
  let prev = 'playing';
  let focusI = 0;
  let started = false;

  function buttons() {
    const m = modals[state];
    return m ? m.querySelectorAll('.btn') : [];
  }
  function refocus() {
    const bs = buttons();
    for (let i = 0; i < bs.length; i++) bs[i].classList.toggle('on', i === focusI);
  }

  /** Title -> warrior -> battlefield -> muster -> the field. */
  const CHAIN = ['title', 'warriors', 'arena', 'briefing'];

  function show(next) {
    if (next === state) return state;
    if (state === 'options') prev = prev; else if (next === 'options') prev = state;
    select.leave(state, next);
    for (const k in modals) modals[k].classList.toggle('open', k === next);
    state = next;
    focusI = 0;
    if (next === 'options') sync();
    select.enter(next);
    refocus();
    onState(state);
    return state;
  }

  /**
   * Is the currently-selected warrior actually available yet?
   *
   * The ladder grants weapons from rank 2 (see GRANTS in game/progress.js).
   * The gate is enforced HERE, at selection, and deliberately not inside
   * `player.setWeapon()` — a runtime refusal would break every capture
   * scenario and contract check that switches weapons to photograph them, and
   * would be the wrong place anyway: this is a question about what the player
   * has earned, not about what the combat system can do.
   */
  function lockedHero() {
    const h = select.hero();
    const pr = world.progress;
    if (!h || !pr || !pr.unlocked) return null;
    if (pr.unlocked(h.moveset)) return null;
    const g = (pr.grants || []).find((x) => x.ref === h.moveset);
    return { hero: h, grant: g, rank: pr.rank };
  }

  /** One step deeper into the muster chain; past the end is the battle. */
  function forward(from) {
    const i = CHAIN.indexOf(from);
    if (i < 0) return state;
    /* A locked warrior stops the chain at the warrior screen rather than at
     * muster: refusing after the player has also chosen a battlefield would
     * throw away a choice they had no reason to think was wasted. */
    if (from === 'warrior') {
      const L = lockedHero();
      if (L) {
        sound('ui_back');
        const need = L.grant ? L.grant.rank : '?';
        world.ui?.toast?.(L.hero.name + ' is not yours yet — ' +
          (L.grant ? L.grant.name + ' unlocks at standing ' + need : 'locked') +
          ' (you are ' + L.rank + ')', { shout: true });
        return state;
      }
    }
    if (i >= CHAIN.length - 1) return muster();
    return show(CHAIN[i + 1]);
  }
  /** One step back out of it. */
  function backward(from) {
    const i = CHAIN.indexOf(from);
    sound('ui_back');
    return show(i > 0 ? CHAIN[i - 1] : 'title');
  }

  function act(a) {
    if (a === 'begin') { openSelect(); }
    else if (a === 'options') { sound('ui_confirm'); show('options'); }
    else if (a === 'back') { sound('ui_back'); show(prev === 'options' ? 'playing' : prev); }
    else if (a === 'resume') { sound('ui_back'); show('playing'); }
    else if (a === 'quit') { sound('ui_back'); show('title'); }
    else if (a === 'again') { begin(); }
    else if (a === 'rechoose') { sound('ui_confirm'); openSelect(); }
    else if (a === 'selnext') { sound('ui_confirm'); forward(state); }
    else if (a === 'selback') { backward(state); }
  }

  /**
   * The audio gesture. WebAudio will not start without a real user gesture and
   * this click is the first one the page ever gets, so the unlock has to
   * happen HERE, on the way into warrior select — not later at `muster()`,
   * which is reached by a keypress the browser may not count.
   */
  function openSelect() {
    started = true;
    try { world.audio && world.audio.unlock && world.audio.unlock(); } catch (e) { /* no device */ }
    sound('ui_confirm');
    return show('warriors');
  }

  /** Commit both choices, then hand over to `begin()`. */
  function muster() {
    try { applyLoadout(world, select.choice.heroId, select.choice.arenaId); }
    catch (e) { console.warn('[ui] loadout failed', e); }
    return begin();
  }

  function begin() {
    started = true;
    try { world.audio && world.audio.unlock && world.audio.unlock(); } catch (e) { /* no device */ }
    sound('ui_confirm');
    // The title can remain open while the authored armies fight behind it, and
    // results leave mission.started set. Always create a fresh deterministic
    // battle instead of merely hiding the modal.
    world.mission?.reset?.({ start: false });
    world.mission?.start?.({ cinematic: !headless });
    try { world.audio && world.audio.music && world.audio.music('auto'); } catch (e) { /* ignore */ }
    show('playing');
  }

  /* ------------------------------------------------------------- wiring */

  el.addEventListener('pointerdown', (ev) => {
    const b = ev.target.closest ? ev.target.closest('.btn') : null;
    if (b) { ev.preventDefault(); act(b.dataset.a); return; }
    // clicking anywhere on the title opens warrior select (and unlocks audio)
    if (state === 'title' && !ev.target.closest('.seg, .slide')) { ev.preventDefault(); openSelect(); }
  });
  el.addEventListener('pointerover', (ev) => {
    const b = ev.target.closest ? ev.target.closest('.btn') : null;
    if (!b) return;
    const bs = buttons();
    for (let i = 0; i < bs.length; i++) if (bs[i] === b && i !== focusI) { focusI = i; refocus(); sound('ui_hover'); }
  });

  function onKey(ev) {
    const k = ev.key;
    if (k === 'Escape' || k === 'p' || k === 'P') {
      ev.preventDefault();
      if (state === 'playing') { sound('ui_back'); show('pause'); }
      else if (state === 'pause') { sound('ui_back'); show('playing'); }
      else if (state === 'options') act('back');
      else if (CHAIN.indexOf(state) > 0) backward(state);
      return;
    }
    if (state === 'playing') return;
    // On a selection screen the arrows belong to the ROSTER, not to the two
    // buttons in the foot: a player pressing Down there means "next warrior".
    if (select.key(ev, state)) return;
    const bs = buttons();
    if (k === 'ArrowDown' || k === 's' || k === 'S') { ev.preventDefault(); if (bs.length) { focusI = (focusI + 1) % bs.length; refocus(); sound('ui_hover'); } }
    else if (k === 'ArrowUp' || k === 'w' || k === 'W') { ev.preventDefault(); if (bs.length) { focusI = (focusI + bs.length - 1) % bs.length; refocus(); sound('ui_hover'); } }
    else if (k === 'Enter' || k === ' ') { ev.preventDefault(); if (bs[focusI]) act(bs[focusI].dataset.a); }
  }
  if (!headless) window.addEventListener('keydown', onKey);

  /* ------------------------------------------------------------- results */

  function results(d) {
    const D = d || {};
    resTitle.textContent = D.title || 'VICTORY';
    resSub.textContent = D.subtitle || 'Battle Report';
    rankEl.textContent = D.rank || 'S';
    rankNote.textContent = D.note || 'Peerless';
    show('results');
    drawResults(D);
  }

  let lastRes = null;
  function drawResults(D) {
    lastRes = D || lastRes;
    if (!lastRes) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const u = parseFloat(getComputedStyle(host).getPropertyValue('--u')) || 1;
    const w = 130 * u, h = 54 * u;
    const set = (cv, txt, ramp) => {
      if (!cv) return;
      const c = fit(cv, w, h, dpr);
      numeral(c, txt, w, h, { size: h * 0.82, align: 'center', ramp, x: w * 0.5, y: h * 0.52 });
    };
    set(resCv.ko, String(lastRes.kills | 0), 'gold');
    set(resCv.chain, String(lastRes.maxChain | 0), 'ember');
    set(resCv.bases, String(lastRes.bases | 0), 'bone');
    set(resCv.time, lastRes.time || '00:00', 'bone');
  }

  function resize() {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const u = parseFloat(getComputedStyle(host).getPropertyValue('--u')) || 1;
    const c = fit(crestCv, 118 * u, 118 * u, dpr);
    crest(c, 118 * u, 118 * u);
    drawResults(null);
    select.resize();
    sync();
  }

  return {
    el, show, results, resize, sync, begin,
    /** The muster chain, for the harness and for capture scenarios. */
    select, openSelect, muster, forward, backward, CHAIN,
    get state() { return state; },
    get started() { return started; },
    dispose() { if (!headless) window.removeEventListener('keydown', onKey); },
    QUAL, QUALITY, applyQuality, BUS_MAX,
  };
}

export { QUAL, BUS_MAX };
